import { NextPage } from 'next';
import Head from 'next/head';
import React from 'react';

const PrivacyPolicy: NextPage = () => {
  return (
    <div style={styles.wrapper}>
      <Head>
        <title>Privacy Policy | althire.ai</title>
      </Head>
      <div style={styles.container}>
        <h1 style={styles.header as React.CSSProperties}>Privacy Policy</h1>
        <p style={styles.effectiveDate as React.CSSProperties}><strong>Effective Date:</strong> 29th March 2026</p>

        <p style={styles.text}>
          At althire.ai, we value the privacy of our users and are committed to protecting their personal information. 
          This Privacy Policy explains how we collect, use, store, and protect the information you provide when using 
          our AI-powered interview products (the "Service"). By using our Service, you agree to the terms outlined in this 
          Privacy Policy.
        </p>

        <h2 style={styles.subHeader}>1. Information We Collect</h2>
        <p style={styles.text}>
          When you use our AI interview product, we may collect the following types of information:
        </p>
        <ul style={styles.list}>
          <li><strong>Personal Information:</strong> Name, email address, and other contact details provided during registration or profile setup.</li>
          <li>Information voluntarily provided during the interview process, including professional background, job experience, skills, and other related data.</li>
          <li><strong>Interview Conversations:</strong> Audio, video, and text of your interview conversations with our AI agents.</li>
          <li>Your answers to interview questions, behavioral responses, and overall interaction with the AI agent.</li>
          <li><strong>Technical Information:</strong> Information about your device, such as your IP address, browser type, device identifiers, and usage data collected via cookies and similar technologies.</li>
          <li><strong>Mobile Phone Number:</strong> Provided by you during job applications or account creation for the purpose of receiving interview invitations and reminders via SMS.</li>
        </ul>

        <h2 style={styles.subHeader}>2. How We Use Your Information</h2>
        <p style={styles.text}>
          We use the information collected from you to provide, maintain, and improve our AI interview services, specifically:
        </p>
        <ul style={styles.list}>
          <li><strong>Conduct and Analyze Interviews:</strong> Assess your skills, behaviors, and qualifications for specific roles.</li>
          <li><strong>Generate Reports:</strong> Create detailed reports about your performance, strengths, and areas of improvement.</li>
          <li><strong>Improve Our AI Technology:</strong> Anonymize interview conversations to train and enhance our AI agents.</li>
          <li><strong>Communications:</strong> Send updates, notifications, and responses regarding our Service.</li>
        </ul>

        <h2 style={styles.subHeader}>3. Legal Basis for Processing</h2>
        <p style={styles.text}>We process personal data based on the following legal grounds:</p>
        <ul style={styles.list}>
          <li><strong>Consent:</strong> User consent is obtained for processing personal data.</li>
          <li><strong>Legitimate Interests:</strong> Data processing is necessary for our legitimate interests while ensuring that these interests do not override your rights.</li>
        </ul>

        <h2 style={styles.subHeader}>4. Data Sharing and Disclosure</h2>
        <p style={styles.text}>
          We do not sell or rent your personal information. We may share your data under the following circumstances:
        </p>
        <ul style={styles.list}>
          <li><strong>With Employers and Recruiters:</strong> Upon your consent.</li>
          <li><strong>Service Providers:</strong> Trusted third-party vendors or APIs who assist in operational tasks while maintaining confidentiality.</li>
          <li><strong>Legal Requirements:</strong> If required by law or in response to valid legal requests.</li>
        </ul>

        <h2 style={styles.subHeader}>5. AI-Specific Disclosures</h2>
        <p style={styles.text}>Users should be informed about:</p>
        <ul style={styles.list}>
          <li>Potential AI errors in decision-making and assessments</li>
          <li>Any unfortunate unhealthy comments or statements made by AI</li>
          <li>Procedures for reporting grievances regarding AI interactions</li>
          <li>Efforts undertaken to mitigate biases in the AI system's algorithms</li>
        </ul>

        <h2 style={styles.subHeader}>6. Data Security Measures</h2>
        <p style={styles.text}>
          We implement appropriate technical and organizational measures to ensure a level of security appropriate to the risk of processing personal data.
        </p>

        <h2 style={styles.subHeader}>7. Indemnification</h2>
        <p style={styles.text}>
          Users indemnify althire.ai against claims arising from:
        </p>
        <ul style={styles.list}>
          <li>Incorrect information provided by users during interviews</li>
          <li>AI errors leading to misjudgments or inaccuracies in assessments</li>
          <li>Disrespectful treatment or inappropriate engagement by the AI during interviews</li>
          <li>Data breaches resulting from API vulnerabilities or third-party access</li>
        </ul>

        <h2 style={styles.subHeader}>8. International Data Transfers</h2>
        <p style={styles.text}>
          If applicable, we will disclose whether personal data will be transferred outside the country and describe the safeguards in place (e.g., Standard Contractual Clauses).
        </p>

        <h2 style={styles.subHeader}>9. Changes to This Privacy Policy</h2>
        <p style={styles.text}>
          We may update this policy periodically. Users will be notified of significant changes through our website or via email.
        </p>

        <h2 style={styles.subHeader}>10. Contact Information</h2>
        <p style={styles.text}>
          For any questions about this Privacy Policy or to exercise your rights under General Data Protection Regulation (GDPR), please contact us at:
        </p>
        <p style={styles.contact}><strong>Email:</strong> contact@althire.ai</p>

        <p style={styles.lastUpdated}><strong>Last Updated:</strong> 29th March 2026</p>

        <h2 style={styles.subHeader}>Proctoring and Cheating Protection</h2>
        <p style={styles.text}>
          At althire.ai, we are committed to maintaining the integrity of our AI-powered interview process. To ensure a fair and unbiased evaluation, we implement proctoring measures during interviews, which may include:
        </p>
        <ul style={styles.list}>
          <li><strong>Monitoring:</strong> Our AI system may monitor interview sessions through video and audio recordings to detect any irregularities or suspicious behavior that could indicate cheating or dishonesty.</li>
          <li><strong>Behavioral Analysis:</strong> The AI may analyze candidate behavior during the interview, including facial expressions, eye movement, and response patterns, to identify potential attempts at cheating.</li>
          <li><strong>Environment Checks:</strong> Candidates may be required to confirm their interview environment is free from unauthorized materials or assistance. This may involve a brief scan of the surroundings via camera.</li>
        </ul>
        <p style={styles.text}>
          By participating in our interview process, you consent to these proctoring measures. We take your privacy seriously and will only use the data collected during proctoring for the purpose of ensuring the integrity of the interview process. Any data collected will be handled in accordance with our Privacy Policy and applicable data protection laws.
        </p>
        <p style={styles.text}>
          If you have any questions or concerns regarding our proctoring measures or how we protect against cheating, please contact us at <strong>contact@althire.ai</strong>.
        </p>

        <h2 style={styles.subHeader}>SMS / Text Messaging Terms & Conditions</h2>
        <p style={styles.text}>
          Althire may collect your mobile phone number when you provide it during the job application or account creation process. We use your mobile number solely to send you transactional SMS messages for job interview invitations and reminders on behalf of our clients (such as Nursedash, Microsoft, and other companies).
        </p>

        <p style={styles.text}>
          <strong>Message and data rates may apply.</strong> Message frequency varies based on your interview schedule (typically 1–4 messages per interview process). Wireless carriers are not liable for delayed or undelivered messages.
        </p>

        <p style={styles.text}>
          You can opt out of receiving SMS messages at any time by replying <strong>STOP</strong> to any message we send. You may also reply <strong>HELP</strong> for assistance.
        </p>

        <p style={styles.text}>
          <strong>Important:</strong> Your mobile phone number and SMS opt-in consent data are never sold, rented, shared, or used for any marketing purposes. This data is used exclusively for sending transactional interview-related messages and is excluded from all other data-sharing categories described in this Privacy Policy.
        </p>

        <p style={styles.text}>
          For full details, see our <a href="/privacy-policy" style={{color: '#00aaff'}}>Privacy Policy</a>.
        </p>
      </div>
    </div>
  );
};

const styles = {
  wrapper: {
    backgroundColor: '#1a1a1a',
    minHeight: '100vh',
    width: '100%',
    padding: '20px 0',
  },
  container: {
    padding: '40px 20px',
    maxWidth: '900px',
    margin: '0 auto',
    fontFamily: 'Arial, sans-serif',
    lineHeight: '1.6',
    borderRadius: '8px',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
    backgroundColor: '#1a1a1a',
    color: '#ffffff',
  },
  header: {
    textAlign: 'center',
    fontSize: '2.4rem',
    marginBottom: '20px',
    color: '#ffffff', // Light text color
  },
  effectiveDate: {
    textAlign: 'center',
    fontSize: '1.1rem',
    marginBottom: '20px',
    color: '#cccccc', // Light gray color for subtle text
  },
  subHeader: {
    fontSize: '1.6rem',
    color: '#00aaff', // Bright blue for headings
    marginTop: '30px',
  },
  text: {
    fontSize: '1.1rem',
    color: '#dddddd', // Light gray text color
    marginBottom: '15px',
  },
  list: {
    paddingLeft: '20px',
    marginBottom: '15px',
    fontSize: '1.1rem',
    color: '#dddddd', // Light gray text color for list items
  },
  contact: {
    fontSize: '1.1rem',
    color: '#cccccc', // Light gray color for contact info
  },
  lastUpdated: {
    fontSize: '1.1rem',
    marginTop: '20px',
    color: '#cccccc', // Light gray color for last updated text
  }
};

export default PrivacyPolicy;
